import { useState, useMemo, useEffect } from "react";
import useSWR from "swr";
import { api, fmtEUR, fmtDate, apiUrl } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "@/components/ui/table";
import { Printer, MagnifyingGlass, FilePdf, Eye, Trash, Envelope, Copy, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import InvoiceDetail from "@/components/InvoiceDetail";
import Pagination from "@/components/Pagination";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const fetcher = (url) => api.get(url).then((r) => r.data);

function DocumentTypeBadge({ type }) {
    const map = {
        facture: { label: "Facture", cls: "text-emerald-700 border-emerald-300 bg-emerald-50" },
        devis: { label: "Devis", cls: "text-blue-700 border-blue-300 bg-blue-50" },
        devis_accepte: { label: "Devis accepté", cls: "text-teal-700 border-teal-300 bg-teal-50" },
        avoir: { label: "Avoir", cls: "text-fuchsia-700 border-fuchsia-300 bg-fuchsia-50" },
        bon_de_commande: { label: "Bon cmd.", cls: "text-indigo-700 border-indigo-300 bg-indigo-50" },
        bon_livraison: { label: "Bon livr.", cls: "text-cyan-700 border-cyan-300 bg-cyan-50" },
        bon_intervention: { label: "Bon interv.", cls: "text-violet-700 border-violet-300 bg-violet-50" },
        traite: { label: "Traite", cls: "text-orange-700 border-orange-300 bg-orange-50" },
        autre: { label: "Autre", cls: "text-slate-700 border-slate-300 bg-slate-50" },
        justificatif: { label: "Justificatif", cls: "text-amber-700 border-amber-300 bg-amber-50" },
        missing: { label: "Aucun doc.", cls: "text-muted-foreground border-border" },
        unknown: { label: "Inconnu", cls: "text-muted-foreground border-border" },
    };
    const info = map[type] || map.unknown;
    return (
        <Badge variant="outline" className={`text-xs ${info.cls}`} data-testid={`doc-type-${type}`}>
            {info.label}
        </Badge>
    );
}

export default function Invoices({ fixedType, title, overline, itemLabel, excludeTypes }) {
    const _overline = overline || "Factures";
    const _title = title || "Toutes vos factures";
    const _itemLabel = itemLabel || { singular: "facture", plural: "factures", feminine: true };
    const listUrl = fixedType ? `/invoices?document_type=${fixedType}` : "/invoices";
    const { data: invoices, mutate } = useSWR(listUrl, fetcher);

    // Per-type column layout. Default (factures) keeps the historical
    // columns. BL/BI/Devis acceptés override specific cells with their
    // own meaningful data.
    const isBL = fixedType === "bon_livraison";
    const isBI = fixedType === "bon_intervention";
    const isDevisOK = fixedType === "devis_accepte";
    const hideSellerCol = isBL || isDevisOK;          // drop "Vendeur" col
    const hideSupplierCol = isDevisOK;                // drop "Fournisseur" col for Devis acceptés
    const showNameCol = isBL || isDevisOK;            // dedicated "Nom" col before "Désignation"
    const col3Label = isBI ? "Nom" : (isBL || isDevisOK) ? "Désignation" : "Commande";
    const col6Label = isBI ? "Symptômes" : "Fournisseur";
    const col7Label = isBI ? "Téléphone" : "Vendeur";

    const formatItems = (items) => {
        if (!items || !items.length) return "—";
        let titles = items.map((it) => (it.title || it.description || "").trim()).filter(Boolean);
        // BLs often have items prefixed with "FACTURE N°XXX" or "Facture 123 —
        // …" referring to the original invoice. Strip that prefix so the
        // designation column only shows the actual product/service description.
        const FACTURE_RE = /^\s*(facture|invoice|fact\.?)\s*(n[°o]?|num[ée]?ro|#)?\s*[:\-]?\s*[\w\-./]*\s*[-—:|·]?\s*/i;
        titles = titles
            .map((t) => t.replace(FACTURE_RE, "").trim())
            .filter(Boolean);
        if (!titles.length) return "—";
        const joined = titles.slice(0, 3).join(" · ");
        return titles.length > 3 ? `${joined} · +${titles.length - 3}` : joined;
    };
    const [query, setQuery] = useState("");
    const [yearFilter, setYearFilter] = useState("all");
    const [monthFilter, setMonthFilter] = useState("all");
    // Custom date range filter. When both are empty the filter is inactive.
    // When set it OVERRIDES year/month so the user can select an arbitrary
    // window (e.g. 01/06/2026 → 15/06/2026).
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    // Pending values reflect what's currently in the pickers (before the
    // user clicks Appliquer). Applied state above filters the table.
    const [dateFromPending, setDateFromPending] = useState("");
    const [dateToPending, setDateToPending] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [docTypeFilter, setDocTypeFilter] = useState("all");
    const [supplierFilter, setSupplierFilter] = useState("all");
    const [selected, setSelected] = useState(new Set());
    const [detailId, setDetailId] = useState(null);
    const [requestOpen, setRequestOpen] = useState(false);
    const [requestData, setRequestData] = useState(null);
    const [wipeOpen, setWipeOpen] = useState(false);
    const [wipeLoading, setWipeLoading] = useState(false);

    // Pagination (20 rows per page; reset to page 1 when filters change)
    const [page, setPage] = useState(1);
    const PER_PAGE = 20;

    const wipeAll = async () => {
        setWipeLoading(true);
        try {
            const params = {};
            if (fixedType) {
                // Restricted page (BL/BI/Devis acceptés) → wipe only that type
                params.document_type = fixedType;
            } else if (excludeTypes?.length) {
                // /factures → exclude the types that have their own rubric so
                // the user doesn't accidentally nuke BL/BI/Devis acceptés
                params.exclude_document_types = excludeTypes.join(",");
            }
            const { data } = await api.delete("/invoices", { params });
            toast.success(`${data.deleted} document${data.deleted > 1 ? "s" : ""} supprimé${data.deleted > 1 ? "s" : ""} (${data.files_removed} PDF effacés)`);
            setSelected(new Set());
            await mutate();
            setWipeOpen(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec de la suppression");
        } finally {
            setWipeLoading(false);
        }
    };

    const wipeByType = async (docType, label) => {
        if (!window.confirm(`Effacer tous les ${label} ?`)) return;
        try {
            const { data } = await api.delete("/invoices", { params: { document_type: docType } });
            toast.success(`${data.deleted} ${label} supprimé${data.deleted > 1 ? "s" : ""}`);
            setSelected(new Set());
            await mutate();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Échec de la suppression");
        }
    };

    // Detect supplier key from any of: marketplace, vendor, supplier, seller
    const getSupplierKey = (inv) => {
        const fields = [inv?.marketplace, inv?.vendor, inv?.supplier, inv?.seller]
            .filter(Boolean).map(s => String(s).toLowerCase());
        const blob = fields.join(" ");
        if (blob.includes("ovh")) return "ovh";
        if (blob.includes("emergent")) return "emergent";
        if (blob.includes("amazon")) return "amazon";
        if (blob.includes("cdiscount")) return "cdiscount";
        if (blob.includes("fnac")) return "fnac";
        return fields[0] || "inconnu";
    };

    const filtered = useMemo(() => {
        if (!invoices) return [];
        const dateRangeActive = !!(dateFrom || dateTo);
        return invoices.filter((inv) => {
            // Custom date range — when set, it OVERRIDES the Year/Month
            // filters (the user wanted a precise window, no extra AND).
            if (dateRangeActive) {
                const od = (inv.order_date || "").slice(0, 10);
                if (!od) return false;
                if (dateFrom && od < dateFrom) return false;
                if (dateTo && od > dateTo) return false;
            } else {
                if (yearFilter !== "all" && !(inv.order_date || "").startsWith(yearFilter)) return false;
                if (monthFilter !== "all") {
                    const mm = (inv.order_date || "").slice(5, 7);
                    if (mm !== String(monthFilter).padStart(2, "0")) return false;
                }
            }
            if (statusFilter !== "all" && inv.status !== statusFilter) return false;
            // /factures excludes the 3 doc types that have their own dedicated
            // rubric (BL / BI / Devis acceptés) so they don't pollute the
            // Factures list or its counters.
            if (excludeTypes?.length && excludeTypes.includes(inv.document_type || "facture")) return false;
            if (docTypeFilter !== "all" && (inv.document_type || "facture") !== docTypeFilter) return false;
            if (supplierFilter !== "all") {
                if (getSupplierKey(inv) !== supplierFilter) return false;
            }
            if (query) {
                const q = query.toLowerCase().trim();
                // Build a haystack covering identifiers, supplier, items,
                // amounts AND the full text indexed from the PDF +
                // intervention block — so a keyword inside the document
                // body (e.g. a customer name on a BI, a SKU on a BL) matches.
                const itemsText = (inv.items || [])
                    .map((it) => `${it.title || ""} ${it.description || ""}`)
                    .join(" ");
                const amountText = [
                    inv.total_ttc, inv.total_ht, inv.vat,
                    // Same number with comma decimal separator (FR convention)
                    String(inv.total_ttc ?? "").replace(".", ","),
                    String(inv.total_ht ?? "").replace(".", ","),
                ].filter((v) => v !== null && v !== undefined && v !== "").join(" ");
                const interv = inv.intervention || {};
                const intervText = Object.values(interv).filter(Boolean).join(" ");
                const hay = [
                    inv.order_id, inv.invoice_number, inv.seller,
                    inv.vendor, inv.supplier, inv.email_sender, inv.email_subject,
                    amountText, itemsText, intervText, inv.searchable_text || "",
                ].filter(Boolean).join(" ").toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [invoices, query, yearFilter, monthFilter, statusFilter, supplierFilter, docTypeFilter, excludeTypes, dateFrom, dateTo]);

    // Detect supplier key from any of: marketplace, vendor, supplier, seller
    // (declaration moved above `filtered` useMemo to avoid TDZ error)

    const suppliers = useMemo(() => {
        // Always include the supported providers so the filter is usable even
        // before the first import. Add any extra suppliers detected on the
        // existing invoices on top.
        const s = new Set(["amazon", "emergent", "ovh"]);
        (invoices || []).forEach((i) => s.add(getSupplierKey(i)));
        return Array.from(s).sort();
    }, [invoices]);

    const supplierLabel = (key) => {
        if (key === "amazon") return "Amazon";
        if (key === "emergent") return "Emergent";
        if (key === "ovh") return "OVH";
        if (key === "cdiscount") return "Cdiscount";
        if (key === "fnac") return "Fnac";
        if (key === "inconnu") return "Inconnu";
        return key.charAt(0).toUpperCase() + key.slice(1);
    };

    const years = useMemo(() => {
        const s = new Set();
        (invoices || []).forEach((i) => { if (i.order_date) s.add(i.order_date.slice(0, 4)); });
        return Array.from(s).sort().reverse();
    }, [invoices]);

    // Compute the slice of `filtered` to display + auto-snap page back when
    // filters trim the dataset below the current offset.
    // Client-side chronological sort (resilient to mixed date types from
    // older records). Prefer invoice_date (LLM-extracted, most accurate),
    // fall back to order_date, then created_at. Descending.
    const sortedFiltered = useMemo(() => {
        const key = (inv) => {
            const cand = inv.invoice_date || inv.order_date || inv.created_at || "";
            // Normalize to ISO string for safe lexicographic comparison.
            return typeof cand === "string" ? cand : String(cand);
        };
        return [...filtered].sort((a, b) => {
            const ka = key(a), kb = key(b);
            if (ka < kb) return 1;
            if (ka > kb) return -1;
            return 0;
        });
    }, [filtered]);

    const paged = useMemo(
        () => sortedFiltered.slice((page - 1) * PER_PAGE, page * PER_PAGE),
        [sortedFiltered, page]
    );
    useEffect(() => { setPage(1); }, [query, yearFilter, monthFilter, statusFilter, supplierFilter, dateFrom, dateTo]);

    const toggleAll = () => {
        if (selected.size === filtered.length) {
            setSelected(new Set());
        } else {
            setSelected(new Set(filtered.map((i) => i.id)));
        }
    };

    const toggleOne = (id) => {
        const ns = new Set(selected);
        if (ns.has(id)) ns.delete(id); else ns.add(id);
        setSelected(ns);
    };

    const bulkPrint = async () => {
        const selectedInvoices = filtered.filter((i) => selected.has(i.id));
        const withPdf = selectedInvoices.filter((i) => i.has_pdf).map((i) => i.id);
        const skipped = selectedInvoices.length - withPdf.length;

        if (withPdf.length === 0) {
            toast.error(
                "Aucune facture sélectionnée n'a de PDF téléchargé. " +
                "Lancez d'abord un import depuis Paramètres → Amazon Mode cookies."
            );
            return;
        }
        try {
            const res = await api.post("/invoices/bulk-print", withPdf, { responseType: "blob" });
            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = URL.createObjectURL(blob);
            // Open in a new tab — let the user trigger print themselves (some browsers block auto-print)
            const w = window.open(url, "_blank");
            if (!w) {
                // Popup blocked → trigger download instead
                const a = document.createElement("a");
                a.href = url;
                a.download = "factures-amazon.pdf";
                a.click();
            }
            toast.success(
                `${withPdf.length} facture${withPdf.length > 1 ? "s" : ""} combinée${withPdf.length > 1 ? "s" : ""} dans un PDF` +
                (skipped > 0 ? ` (${skipped} ignorée${skipped > 1 ? "s" : ""} — sans PDF)` : "")
            );
        } catch (e) {
            const detail = e.response?.data?.detail || "Erreur lors de l'impression groupée";
            toast.error(detail);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer cette facture ?")) return;
        await api.delete(`/invoices/${id}`);
        toast.success("Facture supprimée");
        mutate();
    };

    const requestFactures = async () => {
        // Build the list of invoice IDs needing a real facture
        const candidates = (invoices || []).filter(
            (i) => i.document_type === "justificatif" || i.document_type === "missing" ||
                   i.document_type === "unknown" || !i.has_pdf
        );
        // If user has manually selected rows, restrict to those
        const ids = selected.size > 0
            ? Array.from(selected).filter((id) => candidates.some((c) => c.id === id))
            : candidates.map((c) => c.id);
        if (ids.length === 0) {
            toast.error("Aucune commande sans facture officielle à signaler.");
            return;
        }
        try {
            const { data } = await api.post("/invoices/request-facture-template", { invoice_ids: ids });
            setRequestData(data);
            setRequestOpen(true);
        } catch (e) {
            toast.error("Erreur lors de la génération du message");
        }
    };

    const copyMessage = async () => {
        if (!requestData?.message) return;
        try {
            await navigator.clipboard.writeText(requestData.message);
            toast.success("Message copié dans le presse-papier");
        } catch {
            toast.error("Copie impossible — sélectionnez manuellement le texte");
        }
    };

    return (
        <div className="px-6 lg:px-10 py-8 max-w-[1400px] mx-auto">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                <div>
                    <p className="overline">{_overline}</p>
                    <h1 className="font-display text-4xl font-bold tracking-tight mt-2">
                        {_title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-2 mt-3">
                        <span
                            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary text-primary-foreground text-sm font-semibold"
                            data-testid="invoice-count-total"
                        >
                            {filtered.length} {filtered.length > 1 ? _itemLabel.plural : _itemLabel.singular}
                        </span>
                        {selected.size > 0 && (
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-amber-100 text-amber-900 text-xs font-medium">
                                {selected.size} sélectionnée{selected.size > 1 ? "s" : ""}
                            </span>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Button
                        onClick={() => setWipeOpen(true)}
                        variant="outline"
                        className="border-destructive/40 text-destructive hover:bg-destructive/10"
                        data-testid="wipe-all-btn"
                    >
                        <Trash size={16} className="mr-2" />
                        Tout effacer
                    </Button>
                    {!fixedType && (
                        <>
                            <Button
                                onClick={() => wipeByType("devis", "devis")}
                                variant="outline"
                                size="sm"
                                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                                data-testid="wipe-devis-btn"
                            >
                                <Trash size={14} className="mr-1.5" />
                                Effacer devis
                            </Button>
                            <Button
                                onClick={() => wipeByType("bon_de_commande", "bons de commande")}
                                variant="outline"
                                size="sm"
                                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                                data-testid="wipe-bon-cmd-btn"
                            >
                                <Trash size={14} className="mr-1.5" />
                                Effacer Bon Cmd.
                            </Button>
                            <Button
                                onClick={() => wipeByType("autre", "autres documents")}
                                variant="outline"
                                size="sm"
                                className="border-slate-300 text-slate-700 hover:bg-slate-50"
                                data-testid="wipe-autre-btn"
                            >
                                <Trash size={14} className="mr-1.5" />
                                Effacer Autre
                            </Button>
                            <Button
                                onClick={() => wipeByType("traite", "traites")}
                                variant="outline"
                                size="sm"
                                className="border-orange-300 text-orange-700 hover:bg-orange-50"
                                data-testid="wipe-traite-btn"
                            >
                                <Trash size={14} className="mr-1.5" />
                                Effacer Traite
                            </Button>
                        </>
                    )}
                    <Button
                        onClick={bulkPrint}
                        disabled={selected.size === 0}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="bulk-print-btn"
                    >
                        <Printer size={16} className="mr-2" />
                        Imprimer en bloc
                    </Button>
                </div>
            </div>

            <Card className="ec-card mb-4">
                <CardContent className="p-4 flex flex-wrap items-center gap-3">
                    <div className="relative flex-1 min-w-[220px]">
                        <MagnifyingGlass size={16} className="absolute left-3 top-2.5 text-muted-foreground" />
                        <Input
                            placeholder={fixedType ? "Rechercher (n°, fournisseur, contenu PDF, client, symptômes…)" : "Rechercher (commande, n° facture, vendeur, contenu PDF…)"}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            className="pl-9"
                            data-testid="invoice-search"
                        />
                    </div>
                    <Select value={yearFilter} onValueChange={(v) => { setYearFilter(v); if (v === "all") setMonthFilter("all"); }}>
                        <SelectTrigger className="w-32" data-testid="year-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Toutes années</SelectItem>
                            {years.map((y) => (
                                <SelectItem key={y} value={y}>{y}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select
                        value={monthFilter}
                        onValueChange={setMonthFilter}
                        disabled={yearFilter === "all"}
                    >
                        <SelectTrigger className="w-32" data-testid="month-filter">
                            <SelectValue placeholder="Tous mois" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous mois</SelectItem>
                            {[
                                "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
                                "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
                            ].map((m, i) => (
                                <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    {/* Custom date range — overrides year/month when set */}
                    <div className="flex items-center gap-1.5">
                        <Input
                            type="date"
                            value={dateFromPending}
                            onChange={(e) => setDateFromPending(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { setDateFrom(dateFromPending); setDateTo(dateToPending); } }}
                            max={dateToPending || undefined}
                            className="w-[150px]"
                            title="Du"
                            data-testid="date-from-filter"
                        />
                        <span className="text-muted-foreground text-xs">→</span>
                        <Input
                            type="date"
                            value={dateToPending}
                            onChange={(e) => setDateToPending(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { setDateFrom(dateFromPending); setDateTo(dateToPending); } }}
                            min={dateFromPending || undefined}
                            className="w-[150px]"
                            title="Au"
                            data-testid="date-to-filter"
                        />
                        <Button
                            size="sm"
                            onClick={() => { setDateFrom(dateFromPending); setDateTo(dateToPending); }}
                            disabled={!dateFromPending && !dateToPending}
                            className="h-9 bg-primary hover:bg-primary/90"
                            data-testid="date-range-apply"
                        >
                            Appliquer
                        </Button>
                        {(dateFrom || dateTo) && (
                            <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => {
                                    setDateFromPending("");
                                    setDateToPending("");
                                    setDateFrom("");
                                    setDateTo("");
                                }}
                                title="Réinitialiser la période"
                                data-testid="date-range-clear"
                                className="h-9 w-9"
                            >
                                <X size={14} />
                            </Button>
                        )}
                    </div>
                    {!fixedType && (
                        <Select value={docTypeFilter} onValueChange={setDocTypeFilter}>
                            <SelectTrigger className="w-44" data-testid="doc-type-filter">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les types</SelectItem>
                                <SelectItem value="facture">Factures</SelectItem>
                                <SelectItem value="avoir">Avoirs</SelectItem>
                                <SelectItem value="devis">Devis</SelectItem>
                                {!excludeTypes?.includes("devis_accepte") && (
                                    <SelectItem value="devis_accepte">Devis acceptés</SelectItem>
                                )}
                                <SelectItem value="bon_de_commande">Bons de commande</SelectItem>
                                {!excludeTypes?.includes("bon_livraison") && (
                                    <SelectItem value="bon_livraison">Bons de livraison</SelectItem>
                                )}
                                {!excludeTypes?.includes("bon_intervention") && (
                                    <SelectItem value="bon_intervention">Bons d&apos;intervention</SelectItem>
                                )}
                                <SelectItem value="traite">Traites</SelectItem>
                                <SelectItem value="autre">Autres</SelectItem>
                            </SelectContent>
                        </Select>
                    )}
                    <Select value={supplierFilter} onValueChange={setSupplierFilter}>
                        <SelectTrigger className="w-44" data-testid="supplier-filter">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous fournisseurs</SelectItem>
                            {suppliers.map((s) => (
                                <SelectItem key={s} value={s}>{supplierLabel(s)}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </CardContent>
            </Card>

            <Card className="ec-card overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-accent/40 hover:bg-accent/40">
                            <TableHead className="w-10">
                                <Checkbox
                                    checked={filtered.length > 0 && selected.size === filtered.length}
                                    onCheckedChange={toggleAll}
                                    data-testid="select-all"
                                />
                            </TableHead>
                            <TableHead>{fixedType ? "N°" : "N° facture"}</TableHead>
                            {showNameCol && <TableHead>Nom</TableHead>}
                            <TableHead>{col3Label}</TableHead>
                            <TableHead>Date</TableHead>
                            <TableHead>Type</TableHead>
                            {!hideSupplierCol && <TableHead>{col6Label}</TableHead>}
                            {!hideSellerCol && <TableHead>{col7Label}</TableHead>}
                            <TableHead>Statut</TableHead>
                            <TableHead className="text-right">Montant</TableHead>
                            <TableHead className="w-32"></TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={hideSupplierCol ? 9 : 10} className="text-center py-10 text-muted-foreground text-sm">
                                    Aucun document ne correspond à vos critères.
                                </TableCell>
                            </TableRow>
                        ) : (
                            paged.map((inv) => (
                                <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                                    <TableCell>
                                        <Checkbox
                                            checked={selected.has(inv.id)}
                                            onCheckedChange={() => toggleOne(inv.id)}
                                            data-testid={`select-${inv.id}`}
                                        />
                                    </TableCell>
                                    <TableCell className="font-mono text-xs">
                                        {inv.invoice_number || <span className="text-muted-foreground">—</span>}
                                    </TableCell>
                                    {showNameCol && (
                                        <TableCell className="text-xs max-w-[180px]">
                                            {(() => {
                                                const name = inv.intervention?.customer_name?.trim();
                                                return name
                                                    ? <span className="font-semibold truncate block" title={name}>{name}</span>
                                                    : <span className="text-muted-foreground">—</span>;
                                            })()}
                                        </TableCell>
                                    )}
                                    <TableCell className="text-xs max-w-[280px]">
                                        {(() => {
                                            // BI : Nom client (col3 = "Nom" pour les BI)
                                            if (isBI) {
                                                const name = inv.intervention?.customer_name?.trim();
                                                return name
                                                    ? <span className="font-semibold" title={name}>{name}</span>
                                                    : <span className="text-muted-foreground">—</span>;
                                            }
                                            // BL / Devis acceptés : désignation pure (Nom géré dans la colonne dédiée)
                                            if (isBL || isDevisOK) {
                                                const desc = formatItems(inv.items);
                                                return <span className="truncate block" title={desc}>{desc}</span>;
                                            }
                                            // Default (factures) : référence commande ou sujet du mail IMAP
                                            const oid = inv.order_id || "";
                                            if (oid.startsWith("divers-")) {
                                                return (
                                                    <span className="truncate block" title={inv.email_subject || oid}>
                                                        {inv.email_subject || oid.slice(7, 17) + "…"}
                                                    </span>
                                                );
                                            }
                                            return <span className="font-mono">{oid}</span>;
                                        })()}
                                    </TableCell>
                                    <TableCell className="whitespace-nowrap">
                                        {fmtDate(inv.order_date)}
                                        {inv.date_unknown && (
                                            <span className="ml-1 text-xs text-amber-600" title="Date non retrouvée — celle d'aujourd'hui par défaut">?</span>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <DocumentTypeBadge type={inv.document_type} />
                                        {inv.printed_at && (
                                            <button
                                                onClick={async () => {
                                                    await api.post(`/invoices/${inv.id}/toggle-printed`);
                                                    mutate();
                                                }}
                                                title={`Traitée le ${new Date(inv.printed_at).toLocaleString("fr-FR")} — cliquer pour annuler`}
                                                className="ml-2 inline-block h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-emerald-200 hover:ring-emerald-300 transition align-middle"
                                                aria-label="Traitée"
                                                data-testid={`printed-badge-${inv.id}`}
                                            />
                                        )}
                                        {!inv.printed_at && (
                                            <button
                                                onClick={async () => {
                                                    await api.post(`/invoices/${inv.id}/toggle-printed`);
                                                    mutate();
                                                }}
                                                title="Non traitée — cliquer pour marquer comme traitée"
                                                className="ml-2 inline-block h-2.5 w-2.5 rounded-full bg-amber-500 ring-2 ring-amber-200 hover:ring-amber-300 transition align-middle"
                                                aria-label="Non traitée"
                                                data-testid={`mark-printed-${inv.id}`}
                                            />
                                        )}
                                    </TableCell>
                                    {!hideSupplierCol && (
                                        <TableCell>
                                            {isBI ? (
                                                (() => {
                                                    const s = inv.intervention?.symptoms?.trim();
                                                    return s
                                                        ? <span className="text-xs text-foreground line-clamp-2 max-w-[260px]" title={s}>{s}</span>
                                                        : <span className="text-muted-foreground text-xs">—</span>;
                                                })()
                                            ) : (
                                                <Badge variant="outline" className="text-xs bg-accent/40 font-semibold">
                                                    {supplierLabel(getSupplierKey(inv))}
                                                </Badge>
                                            )}
                                        </TableCell>
                                    )}
                                    {!hideSellerCol && (
                                        <TableCell className="text-sm">
                                            {isBI
                                                ? (inv.intervention?.customer_phone?.trim()
                                                    ? <span className="font-mono tabular" title="Téléphone client">{inv.intervention.customer_phone}</span>
                                                    : <span className="text-muted-foreground">—</span>)
                                                : (inv.seller || "—")}
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <Badge
                                            variant="outline"
                                            className={
                                                inv.has_pdf
                                                    ? "text-emerald-700 border-emerald-200"
                                                    : "text-amber-700 border-amber-200"
                                            }
                                        >
                                            {inv.has_pdf ? "Complet" : "PDF manquant"}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-right font-display font-bold tabular">
                                        {fmtEUR(inv.total_ttc, inv.currency)}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-1 justify-end">
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => setDetailId(inv.id)}
                                                data-testid={`view-${inv.id}`}
                                            >
                                                <Eye size={16} />
                                            </Button>
                                            {inv.has_pdf && (
                                                <a
                                                    href={apiUrl(`/invoices/${inv.id}/pdf`)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid={`pdf-${inv.id}`}
                                                >
                                                    <Button size="icon" variant="ghost">
                                                        <FilePdf size={16} />
                                                    </Button>
                                                </a>
                                            )}
                                            <Button
                                                size="icon"
                                                variant="ghost"
                                                onClick={() => remove(inv.id)}
                                                data-testid={`delete-${inv.id}`}
                                            >
                                                <Trash size={16} className="text-destructive" />
                                            </Button>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
                {filtered.length > PER_PAGE && (
                    <div className="px-4 pb-4">
                        <Pagination
                            page={page}
                            total={filtered.length}
                            perPage={PER_PAGE}
                            onChange={setPage}
                            testIdPrefix="invoices-pagination"
                        />
                    </div>
                )}
            </Card>

            <InvoiceDetail
                invoiceId={detailId}
                open={!!detailId}
                onOpenChange={(v) => !v && setDetailId(null)}
            />

            <Dialog open={requestOpen} onOpenChange={setRequestOpen}>
                <DialogContent className="max-w-2xl" data-testid="request-facture-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-display text-2xl tracking-tight">
                            Demander les factures officielles
                        </DialogTitle>
                        <DialogDescription>
                            {requestData?.count
                                ? `Message pré-rédigé pour ${requestData.count} commande${requestData.count > 1 ? "s" : ""}. Copiez-le, puis ouvrez le service client Amazon pour l’envoyer.`
                                : "Aucune commande à signaler."}
                        </DialogDescription>
                    </DialogHeader>

                    {requestData && (
                        <div className="space-y-4">
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                                    Objet
                                </label>
                                <div className="font-mono text-sm bg-accent/40 border border-border rounded-md p-2 mt-1">
                                    {requestData.subject}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-widest">
                                    Message
                                </label>
                                <Textarea
                                    value={requestData.message}
                                    readOnly
                                    rows={14}
                                    className="font-mono text-xs mt-1"
                                    data-testid="request-message"
                                />
                            </div>
                            <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3 text-xs text-amber-800">
                                <strong>Procédure :</strong> 1) Copiez le message ci-dessus. 2) Cliquez sur « Ouvrir le service client Amazon »,
                                puis choisissez « Un problème avec une commande » → « Facture demandée ». 3) Collez le message et envoyez.
                            </div>
                        </div>
                    )}

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={() => setRequestOpen(false)} data-testid="request-close">
                            Fermer
                        </Button>
                        <Button variant="outline" onClick={copyMessage} data-testid="request-copy">
                            <Copy size={16} className="mr-2" />
                            Copier le message
                        </Button>
                        {requestData && (
                            <a href={requestData.amazon_contact_url} target="_blank" rel="noreferrer" data-testid="request-open-amazon">
                                <Button className="bg-primary hover:bg-primary/90">
                                    <Envelope size={16} className="mr-2" />
                                    Ouvrir le service client Amazon
                                </Button>
                            </a>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={wipeOpen} onOpenChange={setWipeOpen}>
                <DialogContent className="max-w-md" data-testid="wipe-confirm-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-destructive">
                            <Trash size={20} />
                            Effacer tous les {_itemLabel.plural} ?
                        </DialogTitle>
                        <DialogDescription>
                            Cette action supprime <strong>{invoices?.length || 0}</strong> {_itemLabel.plural}
                            (et leurs PDFs). Elle est <strong>irréversible</strong>.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={() => setWipeOpen(false)}
                            data-testid="wipe-cancel"
                            disabled={wipeLoading}
                        >
                            Annuler
                        </Button>
                        <Button
                            onClick={wipeAll}
                            disabled={wipeLoading}
                            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
                            data-testid="wipe-confirm"
                        >
                            <Trash size={16} className="mr-2" />
                            {wipeLoading ? "Suppression…" : "Tout effacer"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
